import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from "@mui/material";
import { Upload } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

/**
 * Shared upload widget used by both document-management panels.
 *
 * The "primary" target is implicit (whichever panel hosts this widget). An
 * optional `secondaryTarget` renders a single checkbox inside the dropzone
 * so the same file can also be sent to the other pool. The caller decides
 * the wording — keep it plain (e.g. "Also add to Clinical References").
 *
 * `acceptedLabel` replaces the raw extension list with plain wording, for
 * screens whose users shouldn't have to read ".odt, .pptx".
 */
const DocumentUploadZone = ({
  disabled = false,
  hasPromptId = true,
  acceptedExtensions = [],
  acceptedLabel = null,
  secondaryTarget = null,
  onFilesAccepted,
  inputId = "document-upload",
  trailing = null,
  sx,
}) => {
  const { t } = useTranslation("masterchart");
  const [isDragging, setIsDragging] = useState(false);
  const [includeSecondary, setIncludeSecondary] = useState(false);

  // Reset the secondary toggle if the caller swaps in a different target.
  useEffect(() => {
    setIncludeSecondary(false);
  }, [secondaryTarget?.value]);

  const inputDisabled = disabled || !hasPromptId;

  const handleFiles = (files) => {
    if (!files?.length || inputDisabled) return;
    onFilesAccepted?.(files, includeSecondary);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFiles(Array.from(event.dataTransfer.files || []));
  };

  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: "center",
          borderStyle: "dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          bgcolor: isDragging ? "action.hover" : "background.paper",
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!inputDisabled) setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
      >
        <Upload sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
        <Typography variant="subtitle1">{t("uploadZone.title")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("uploadZone.dragHint")}
        </Typography>
        {acceptedLabel || acceptedExtensions.length ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            {acceptedLabel ||
              t("uploadZone.accepted", { extensions: acceptedExtensions.join(", ") })}
          </Typography>
        ) : null}
        <input
          id={inputId}
          type="file"
          multiple
          accept={acceptedExtensions.join(",")}
          style={{ display: "none" }}
          onChange={(event) => {
            handleFiles(Array.from(event.target.files || []));
            event.target.value = "";
          }}
          disabled={inputDisabled}
        />
        <label htmlFor={inputId}>
          <Button
            component="span"
            variant="outlined"
            startIcon={<Upload />}
            disabled={inputDisabled}
          >
            {t("uploadZone.selectFiles")}
          </Button>
        </label>
      </Paper>

      {secondaryTarget || trailing ? (
        <Box
          sx={{
            mt: 0.5,
            pl: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {secondaryTarget ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeSecondary}
                  onChange={(event) => setIncludeSecondary(event.target.checked)}
                  disabled={disabled}
                />
              }
              label={secondaryTarget.label}
            />
          ) : <span />}
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
};

export default DocumentUploadZone;
